import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import { useClerk, useOrganization, useUser } from "@clerk/nextjs"
import {
  isClerkAPIResponseError,
  isReverificationCancelledError,
} from "@clerk/nextjs/errors"

import { logger } from "@/lib/logger"
import { useDebounce } from "@/hooks/useDebounce"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { useInvalidateOrganizationUsers } from "../transport"
import type { MemberManagementActions, MemberSummary } from "../types"
import {
  getInvitationEmailError,
  parseInvitationEmails,
} from "../utils/invitationEmails"

const PAGE_SIZE = 50
const log = logger("OrganizationUserManagement")

function readableError(error: unknown): Error | null {
  if (error === null || error === undefined) {
    return null
  }
  if (isClerkAPIResponseError(error)) {
    const detail = error.errors[0]
    return new Error(detail?.longMessage || detail?.message || error.message)
  }
  return error instanceof Error
    ? error
    : new Error("Could not load organization access. Please try again.")
}

export function useOrganizationUserManagement(
  verifyWrite?: (write: () => Promise<unknown>) => Promise<unknown>,
) {
  const router = useRouter()
  const clerk = useClerk()
  const { user } = useUser()
  const { isLoaded, organization, membership } = useOrganization()
  const routeSlug = getRouteOrganizationSlug(router.asPath)
  const scopeReady = !!(
    isLoaded &&
    organization &&
    routeSlug &&
    organization.slug === routeSlug
  )
  const canManage =
    scopeReady &&
    membership?.organization.id === organization?.id &&
    !!membership?.permissions.includes("org:sys_memberships:manage")
  const [search, setSearch] = useState("")
  const query = useDebounce(search.trim(), 250)
  const { memberships, invitations } = useOrganization({
    memberships: scopeReady
      ? {
          pageSize: PAGE_SIZE,
          ...(query ? { query } : {}),
          keepPreviousData: true,
        }
      : undefined,
    invitations:
      scopeReady && canManage
        ? { pageSize: PAGE_SIZE, status: ["pending"], keepPreviousData: true }
        : undefined,
  })
  const { memberships: admins } = useOrganization({
    memberships: scopeReady ? { pageSize: 1, role: ["org:admin"] } : undefined,
  })
  const invalidateUsers = useInvalidateOrganizationUsers()
  const [refreshFailure, setRefreshFailure] = useState<{
    organizationId: string
    slug: string | null
    error: Error
  } | null>(null)
  const refreshError =
    refreshFailure !== null &&
    refreshFailure.organizationId === organization?.id &&
    refreshFailure.slug === routeSlug
      ? refreshFailure.error
      : null
  const currentScope = useRef({
    id: organization?.id,
    slug: routeSlug,
    canManage,
    refreshBlocked: !!refreshError,
  })
  currentScope.current = {
    id: organization?.id,
    slug: routeSlug,
    canManage,
    refreshBlocked: !!refreshError,
  }
  const refreshGeneration = useRef(0)
  useEffect(
    () => () => {
      refreshGeneration.current++
    },
    [organization?.id, routeSlug],
  )
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const memberScopeError = memberships?.data?.some(
    (resource) =>
      resource.organization.id !== organization?.id ||
      !resource.publicUserData?.userId,
  )
    ? new Error("Organization changed. Reload this page before continuing.")
    : null
  const invitationScopeError = invitations?.data?.some(
    (resource) => resource.organizationId !== organization?.id,
  )
    ? new Error("Organization changed. Reload this page before continuing.")
    : null

  const memberData: MemberSummary[] | undefined =
    scopeReady &&
    !memberScopeError &&
    !memberships?.isLoading &&
    !(memberships?.error && !memberships.count)
      ? memberships?.data?.flatMap((resource) => {
          const publicUser = resource.publicUserData
          if (!publicUser?.userId) {
            return []
          }
          return [
            {
              id: publicUser.userId,
              name:
                [publicUser.firstName, publicUser.lastName]
                  .filter(Boolean)
                  .join(" ") || publicUser.identifier,
              email: publicUser.identifier,
              image_url: publicUser.imageUrl || null,
              role: resource.role,
              joined_at: resource.createdAt.toISOString(),
            },
          ]
        })
      : undefined
  const invitationData =
    scopeReady &&
    !invitationScopeError &&
    !invitations?.isLoading &&
    !(invitations?.error && !invitations.count)
      ? invitations?.data?.map((resource) => ({
          id: resource.id,
          email: resource.emailAddress,
          role: resource.role,
          created_at: resource.createdAt.toISOString(),
        }))
      : undefined

  const assertScope = () => {
    if (
      !scopeReady ||
      !organization ||
      !canManage ||
      !mounted.current ||
      currentScope.current.id !== organization.id ||
      currentScope.current.slug !== routeSlug ||
      !currentScope.current.canManage ||
      currentScope.current.refreshBlocked ||
      clerk.organization?.id !== organization.id
    ) {
      throw new Error(
        "Organization changed or access is unavailable. Reload before continuing.",
      )
    }
    return organization
  }

  const refresh = async (
    target: NonNullable<typeof organization>,
    memberChange: boolean,
    message: string,
  ) => {
    const request = ++refreshGeneration.current
    const isCurrent = () =>
      request === refreshGeneration.current &&
      mounted.current &&
      currentScope.current.id === target.id &&
      currentScope.current.slug === routeSlug &&
      clerk.organization?.id === target.id
    if (!isCurrent()) {
      return
    }
    const reads: (() => Promise<unknown> | undefined)[] = [
      () => memberships?.revalidate?.(),
      () => invitations?.revalidate?.(),
      () => admins?.revalidate?.(),
      () => target.reload(),
      ...(memberChange ? [invalidateUsers] : []),
    ]
    const results = await Promise.allSettled(
      reads.map((read) => Promise.resolve().then(read)),
    )
    if (!isCurrent()) {
      return
    }
    const failure = results.find((result) => result.status === "rejected")
    if (failure?.status === "rejected") {
      log.warn("Could not refresh organization access", {
        error: readableError(failure.reason)?.message,
      })
      currentScope.current.refreshBlocked = true
      setRefreshFailure({
        organizationId: target.id,
        slug: routeSlug,
        error: new Error(message),
      })
    } else {
      currentScope.current.refreshBlocked = false
      setRefreshFailure(null)
    }
  }

  const run = async (write: () => Promise<unknown>, memberChange: boolean) => {
    const target = assertScope()
    let writeError: Error | null = null
    try {
      if (verifyWrite) {
        await verifyWrite(write)
      } else {
        await write()
      }
    } catch (error) {
      if (isReverificationCancelledError(error)) {
        throw error
      }
      writeError = readableError(error)
    }
    await refresh(
      target,
      memberChange,
      writeError
        ? "Access data could not refresh. Retry loading before making another change."
        : "Change saved, but access data could not refresh. Retry loading before making another change.",
    )
    if (writeError) {
      throw writeError
    }
  }

  const actions: MemberManagementActions = {
    invite: (email, role) =>
      run(() => {
        const target = assertScope()
        const emailAddresses = parseInvitationEmails(
          typeof email === "string" ? [email] : email,
        )
        const validationError = getInvitationEmailError(emailAddresses)
        if (validationError) {
          throw new Error(validationError)
        }
        return emailAddresses.length === 1
          ? target.inviteMember({ emailAddress: emailAddresses[0], role })
          : target.inviteMembers({ emailAddresses, role })
      }, false),
    changeRole: (member, role) =>
      run(() => {
        assertScope()
        const resource = memberships?.data?.find(
          (item) => item.publicUserData?.userId === member.id,
        )
        if (!resource || resource.organization.id !== organization?.id) {
          throw new Error(
            "This member is no longer on the current page. Reload and try again.",
          )
        }
        return resource.update({ role })
      }, true),
    remove: (member) =>
      run(() => {
        assertScope()
        const resource = memberships?.data?.find(
          (item) => item.publicUserData?.userId === member.id,
        )
        if (!resource || resource.organization.id !== organization?.id) {
          throw new Error(
            "This member is no longer on the current page. Reload and try again.",
          )
        }
        return resource.destroy()
      }, true),
    revoke: (invitation) =>
      run(() => {
        const target = assertScope()
        const resource = invitations?.data?.find(
          (item) => item.id === invitation.id,
        )
        if (!resource || resource.organizationId !== target.id) {
          throw new Error(
            "This invitation is no longer on the current page. Reload and try again.",
          )
        }
        return resource.revoke()
      }, false),
  }

  const retry = async (read: (() => Promise<void>) | undefined) => {
    try {
      if (refreshError && organization) {
        await refresh(organization, true, refreshError.message)
      } else {
        await read?.()
      }
    } catch (error) {
      log.warn("Could not reload organization access", {
        error: readableError(error)?.message,
      })
    }
  }
  const memberError =
    memberScopeError ||
    refreshError ||
    readableError(memberships?.error) ||
    readableError(admins?.error)
  const invitationError =
    invitationScopeError || refreshError || readableError(invitations?.error)
  const membersFetching =
    !scopeReady ||
    query !== search.trim() ||
    !!memberships?.isFetching ||
    !!admins?.isFetching
  return {
    members: {
      data: memberData,
      total: memberData === undefined ? undefined : memberships?.count,
      isFetching: membersFetching,
      error: memberError,
      refetch: () =>
        retry(async () => {
          await Promise.all([
            memberships?.revalidate?.(),
            admins?.revalidate?.(),
          ])
        }),
      hasNextPage: !!memberships?.hasNextPage,
    },
    invitations: {
      data: invitationData,
      total: invitationData === undefined ? undefined : invitations?.count,
      isFetching: !scopeReady || !!invitations?.isFetching,
      error: invitationError,
      refetch: () => retry(invitations?.revalidate),
      hasNextPage: !!invitations?.hasNextPage,
    },
    currentUserId: user?.id ?? "",
    canManage,
    adminCount: admins?.count ?? 0,
    actions,
    search,
    onSearchChange: (value: string) => {
      setSearch(value)
      memberships?.fetchPage?.(1)
    },
    membersPage: memberships?.page ?? 1,
    invitationsPage: invitations?.page ?? 1,
    membersPrevious: () => memberships?.fetchPrevious?.(),
    invitationsPrevious: () => invitations?.fetchPrevious?.(),
    membersNext: () => memberships?.fetchNext?.(),
    invitationsNext: () => invitations?.fetchNext?.(),
    mutationsReady:
      scopeReady &&
      memberData !== undefined &&
      admins?.data !== undefined &&
      !membersFetching &&
      !memberError &&
      !invitationError,
  }
}
